import React, { useRef, useEffect, useCallback, useMemo } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../pixiSettings";
import Overlay from "../../../Games/Shared/Overlay/Overlay";
import MaxWinDialogController from "./MaxWinDialogController";
import TextButton from "../../UserInterface/primary/Buttons/TextButton";
import { safeInit } from "../../../utilities";
import {
  selectShowMaxWin,
  selectHoldAndWinCurrentRound,
  selectBet,
  selectAutoPlaySpeed,
} from "../../../State/selectors";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";

const MaxWinDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showMaxWin = useSelector(selectShowMaxWin);
  const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);
  const bet = useSelector(selectBet);
  const autoPlaySpeed = useSelector(selectAutoPlaySpeed);

  const controllerRef = useRef(null);
  const spineContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const buttonRef = useRef(null);
  const dimensionsContext = useDimensions();
  const { dimensions, symbolHeight, device } = dimensionsContext;

  // Determine button text based on device type
  const continueButtonKey = useMemo(() => {
    return device === PixiSettings.DEVICE.DESKTOP
      ? "click_to_continue"
      : "tap_to_continue";
  }, [device]);

  // Initialize controller once
  useEffect(() => {
    if (spineContainerRef.current && !controllerRef.current) {
      // Set button alpha to 0 for this instance
      if (buttonRef.current) {
        buttonRef.current.alpha = 0;
      }

      safeInit(() => {
        controllerRef.current = new MaxWinDialogController(
          spineContainerRef.current,
          dimensionsContext,
          dispatch,
          symbolHeight,
          overlayRef,
          buttonRef,
          t
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (controllerRef.current && symbolHeight && dimensionsContext) {
      controllerRef.current.updateDimensions(
        dimensionsContext,
        symbolHeight
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolHeight]);

  // Play animation when triggered
  useEffect(() => {
    if (controllerRef.current && showMaxWin) {
      // Get win value from Hold and Win current round
      const winValue = holdAndWinCurrentRound?.win || 0;
      const betValue = bet || 0;
      const isTurboMode = autoPlaySpeed === "turbo";
      controllerRef.current.startMaxWinAnimation(winValue, betValue, isTurboMode);
    }
  }, [showMaxWin, holdAndWinCurrentRound, bet, autoPlaySpeed]);

  // Handle user click to continue animation
  const handleBackgroundClick = useCallback(
    (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      if (controllerRef.current && showMaxWin) {
        const accepted = controllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [showMaxWin]
  );

  // Add spacebar handling
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle spacebar
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Ignore auto-repeat (holding the key)
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      // Only handle when max win is showing AND controller exists
      if (!showMaxWin || !controllerRef.current) {
        return;
      }

      // Prevent event from reaching other handlers
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        const accepted = controllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      } catch (error) {
        // Emergency recovery - try to finish animation
        if (
          controllerRef.current &&
          controllerRef.current.finishAnimation
        ) {
          controllerRef.current.finishAnimation();
        }
      }
    };

    // Use capture phase to ensure we get the event first
    if (showMaxWin) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false, // Allow preventDefault
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [showMaxWin]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
      }
    };
  }, []);

  if (!dimensions || !dimensions.width || !dimensions.height) {
    return null;
  }

  return (
    <Container
      name="max_win_dialog"
      x={dimensions.width / 2}
      y={dimensions.height / 2}
      sortableChildren={true}
      zIndex={9}
      ref={spineContainerRef}
    >
      <Overlay
        ref={overlayRef}
        dimensions={dimensions}
        interactive={showMaxWin}
        onPointerDown={handleBackgroundClick}
        x={-dimensions.width / 2}
        y={-dimensions.height / 2}
        fadeDuration={350}
      />

      <TextButton
        ref={buttonRef}
        y={dimensions.height / 2}
        translationKey={continueButtonKey}
      />
    </Container>
  );
};

export default MaxWinDialog;
